import { Box, Transition } from '@mantine/core';
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

export type ScaleOrigin = 'center' | 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface ScaleInProps {
  children: React.ReactNode;
  visible?: boolean;
  duration?: number;
  delay?: number;
  initialScale?: number;
  origin?: ScaleOrigin;
  timingFunction?: string;
  onEnter?: () => void;
  onEntered?: () => void;
  onExit?: () => void;
  onExited?: () => void;
}

const getTransformOrigin = (origin: ScaleOrigin): string => {
  const map: Record<ScaleOrigin, string> = {
    center: 'center center',
    top: 'center top',
    bottom: 'center bottom',
    left: 'left center',
    right: 'right center',
    'top-left': 'left top',
    'top-right': 'right top',
    'bottom-left': 'left bottom',
    'bottom-right': 'right bottom',
  };
  return map[origin];
};

const ScaleIn = forwardRef<HTMLDivElement, ScaleInProps>(
  (
    {
      children,
      visible = true,
      duration = 300,
      delay = 0,
      initialScale = 0.9,
      origin = 'center',
      timingFunction = 'ease-out',
      onEnter,
      onEntered,
      onExit,
      onExited,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const actualDuration = reducedMotion ? 0 : duration;

    const scaleTransition = {
      in: { opacity: 1, transform: 'scale(1)' },
      out: { opacity: 0, transform: `scale(${initialScale})` },
      common: { transformOrigin: getTransformOrigin(origin) },
      transitionProperty: 'opacity, transform',
    };

    return (
      <Transition
        mounted={visible}
        transition={scaleTransition}
        duration={actualDuration}
        timingFunction={timingFunction}
        onEnter={onEnter}
        onEntered={onEntered}
        onExit={onExit}
        onExited={onExited}
      >
        {(styles) => (
          <Box
            ref={ref}
            style={{
              ...styles,
              transitionDelay: `${delay}ms`,
            }}
          >
            {children}
          </Box>
        )}
      </Transition>
    );
  }
);

ScaleIn.displayName = 'ScaleIn';

export default ScaleIn;
